export const CELL_HIDDEN = 0;
export const CELL_REVEALED = 1;
export const CELL_FLAGGED = 2;

export const GAME_CLEAR_ALL = 'clear_all';
export const GAME_PATH = 'path';
export const GAME_FIND = 'find';

function createRng( seed )
{
    let s = seed | 0;
    return () =>
    {
        s = ( s + 0x6D2B79F5 ) | 0;
        let t = Math.imul( s ^ ( s >>> 15 ), 1 | s );
        t = ( t + Math.imul( t ^ ( t >>> 7 ), 61 | t ) ) ^ t;
        return ( ( t ^ ( t >>> 14 ) ) >>> 0 ) / 4294967296;
    };
}

export class MinesweeperGame
{
    constructor( width = 16, height = 16, mineCount = 5, gameType = GAME_CLEAR_ALL, seed = 1, edgeDecorationLevel = 2 )
    {
        this.width = width;
        this.height = height;
        this.mineCount = mineCount;
        this.gameType = gameType;
        this.edgeDecorationLevel = edgeDecorationLevel;
        this.rng = createRng( seed );
        this.firstClick = true;
        this.gameOver = false;
        this.won = false;

        this.diamondX = -1;
        this.diamondY = -1;

        this.pendingReveals = [];
        this.revealTimestamps = [];

        // Initialize grids
        this.mines = [];
        this.cellState = [];
        this.adjacent = [];
        this.disabled = [];
        this.frozen = [];

        for ( let x = 0; x < width; x++ )
        {
            this.mines[ x ] = [];
            this.cellState[ x ] = [];
            this.adjacent[ x ] = [];
            this.revealTimestamps[ x ] = [];
            this.disabled[ x ] = [];
            this.frozen[ x ] = [];
            for ( let y = 0; y < height; y++ )
            {
                this.mines[ x ][ y ] = false;
                this.cellState[ x ][ y ] = CELL_HIDDEN;
                this.adjacent[ x ][ y ] = 0;
                this.revealTimestamps[ x ][ y ] = 0;
                this.disabled[ x ][ y ] = false;
                this.frozen[ x ][ y ] = false;
            }
        }

        this.generateEdgeDecoration();

        if ( this.gameType === GAME_PATH )
        {
            this.firstClick = false;
            this.initPathMode();
        }
    }

    generateEdgeDecoration()
    {
        if ( this.edgeDecorationLevel <= 0 ) return;

        const isPath = this.gameType === GAME_PATH;
        const maxDisable = this.edgeDecorationLevel + 1;

        // Columns: disable 0–N cells from bottom and top
        for ( let x = 0; x < this.width; x++ )
        {
            const fromBottom = Math.floor( this.rng() * maxDisable );
            const fromTop    = Math.floor( this.rng() * maxDisable );
            for ( let i = 0; i < fromBottom; i++ )
            {
                const y = i;
                if ( isPath && y === 0 ) continue; // preserve start zone
                this.disabled[ x ][ y ] = true;
            }
            for ( let i = 0; i < fromTop; i++ )
            {
                const y = this.height - 1 - i;
                if ( isPath && y === this.height - 1 ) continue; // preserve finish zone
                this.disabled[ x ][ y ] = true;
            }
        }

        // Rows: disable 0–N cells from left and right
        for ( let y = 0; y < this.height; y++ )
        {
            if ( isPath && ( y === 0 || y === this.height - 1 ) ) continue;

            const fromLeft  = Math.floor( this.rng() * maxDisable );
            const fromRight = Math.floor( this.rng() * maxDisable );
            for ( let i = 0; i < fromLeft; i++ )
            {
                this.disabled[ i ][ y ] = true;
            }
            for ( let i = 0; i < fromRight; i++ )
            {
                this.disabled[ this.width - 1 - i ][ y ] = true;
            }
        }
    }

    initPathMode()
    {
        const maxAttempts = 100;

        for ( let attempt = 0; attempt < maxAttempts; attempt++ )
        {
            // Clear mines
            for ( let x = 0; x < this.width; x++ )
                for ( let y = 0; y < this.height; y++ )
                    this.mines[ x ][ y ] = false;

            // Pick a random safe start cell in bottom row (skip disabled)
            const candidates = [];
            for ( let x = 0; x < this.width; x++ )
                if ( !this.disabled[ x ][ 0 ] ) candidates.push( x );
            const startX = candidates[ Math.floor( this.rng() * candidates.length ) ];

            // Place mines randomly, skipping the start cell and disabled cells
            let placed = 0;
            while ( placed < this.mineCount )
            {
                const x = Math.floor( this.rng() * this.width );
                const y = Math.floor( this.rng() * this.height );
                if ( this.disabled[ x ][ y ] ) continue;
                if ( this.mines[ x ][ y ] ) continue;
                if ( x === startX && y === 0 ) continue;
                this.mines[ x ][ y ] = true;
                placed++;
            }

            // BFS from the start cell to check if it can reach top row
            if ( this.hasPathToTop( startX ) )
            {
                // Pre-reveal the start cell
                this.cellState[ startX ][ 0 ] = CELL_REVEALED;
                this.calculateAdjacent();
                return;
            }
        }

        // Fallback: just use current layout (very unlikely to reach here)
        const startX = Math.floor( this.rng() * this.width );
        this.cellState[ startX ][ 0 ] = CELL_REVEALED;
        this.calculateAdjacent();
    }

    hasPathToTop( startX )
    {
        const visited = new Set();
        const queue = [];
        const dirs = [ [ 0, 1 ], [ 0, -1 ], [ 1, 0 ], [ -1, 0 ] ]; // orthogonal only

        // Start from the specific start cell
        queue.push( [ startX, 0 ] );
        visited.add( startX + ',0' );

        while ( queue.length > 0 )
        {
            const [ x, y ] = queue.shift();

            if ( y === this.height - 1 ) return true;

            for ( const [ dx, dy ] of dirs )
            {
                const nx = x + dx;
                const ny = y + dy;
                if ( nx < 0 || nx >= this.width || ny < 0 || ny >= this.height ) continue;
                if ( this.disabled[ nx ][ ny ] ) continue;
                const key = nx + ',' + ny;
                if ( visited.has( key ) ) continue;
                if ( this.mines[ nx ][ ny ] ) continue;
                visited.add( key );
                queue.push( [ nx, ny ] );
            }
        }

        return false;
    }

    placeMines( safeX, safeY )
    {
        let placed = 0;
        while ( placed < this.mineCount )
        {
            const x = Math.floor( this.rng() * this.width );
            const y = Math.floor( this.rng() * this.height );

            // Skip disabled cells
            if ( this.disabled[ x ][ y ] ) continue;

            // Skip if already a mine
            if ( this.mines[ x ][ y ] ) continue;

            // Skip if in safe zone (first click + neighbors)
            if ( Math.abs( x - safeX ) <= 1 && Math.abs( y - safeY ) <= 1 ) continue;

            this.mines[ x ][ y ] = true;
            placed++;
        }

        this.calculateAdjacent();

        if ( this.gameType === GAME_FIND )
        {
            this.placeDiamond( safeX, safeY );
        }
    }

    placeDiamond( safeX, safeY )
    {
        const candidates = [];
        for ( let x = 0; x < this.width; x++ )
        {
            for ( let y = 0; y < this.height; y++ )
            {
                if ( this.disabled[ x ][ y ] ) continue;
                if ( this.mines[ x ][ y ] ) continue;
                if ( Math.abs( x - safeX ) <= 1 && Math.abs( y - safeY ) <= 1 ) continue;
                candidates.push( [ x, y ] );
            }
        }
        const [ dx, dy ] = candidates[ Math.floor( this.rng() * candidates.length ) ];
        this.diamondX = dx;
        this.diamondY = dy;
    }

    getDirectionToDiamond( x, y )
    {
        const dx = this.diamondX + 0.5 - ( x + 0.5 );
        const dy = this.diamondY + 0.5 - ( y + 0.5 );
        const angle = Math.atan2( dy, dx );
        // Map angle to 8 octants
        const octant = Math.round( angle / ( Math.PI / 4 ) );
        const arrows = [ '\u2192', '\u2197', '\u2191', '\u2196', '\u2190', '\u2199', '\u2193', '\u2198' ];
        // octant: 0=right, 1=up-right, 2=up, 3=up-left, ±4=left, -3=down-left, -2=down, -1=down-right
        return arrows[ ( ( octant % 8 ) + 8 ) % 8 ];
    }

    calculateAdjacent()
    {
        for ( let x = 0; x < this.width; x++ )
        {
            for ( let y = 0; y < this.height; y++ )
            {
                if ( this.disabled[ x ][ y ] )
                {
                    this.adjacent[ x ][ y ] = 0;
                    continue;
                }

                if ( this.mines[ x ][ y ] )
                {
                    this.adjacent[ x ][ y ] = -1;
                    continue;
                }

                let count = 0;
                for ( let dx = -1; dx <= 1; dx++ )
                {
                    for ( let dy = -1; dy <= 1; dy++ )
                    {
                        if ( dx === 0 && dy === 0 ) continue;
                        const nx = x + dx;
                        const ny = y + dy;
                        if ( nx >= 0 && nx < this.width && ny >= 0 && ny < this.height && !this.disabled[ nx ][ ny ] )
                        {
                            if ( this.mines[ nx ][ ny ] ) count++;
                        }
                    }
                }
                this.adjacent[ x ][ y ] = count;
            }
        }
    }

    canReveal( x, y )
    {
        if ( this.disabled[ x ][ y ] ) return false;
        if ( this.frozen[ x ][ y ] ) return false;
        if ( this.gameType === GAME_CLEAR_ALL || this.gameType === GAME_FIND ) return true;

        // PATH mode: cell must be adjacent to at least one revealed, non-disabled cell
        for ( let dx = -1; dx <= 1; dx++ )
        {
            for ( let dy = -1; dy <= 1; dy++ )
            {
                if ( dx === 0 && dy === 0 ) continue;
                const nx = x + dx;
                const ny = y + dy;
                if ( nx >= 0 && nx < this.width && ny >= 0 && ny < this.height && !this.disabled[ nx ][ ny ] )
                {
                    if ( this.cellState[ nx ][ ny ] === CELL_REVEALED ) return true;
                }
            }
        }
        return false;
    }

    reveal( x, y )
    {
        if ( this.gameOver ) return;
        if ( this.pendingReveals.length > 0 ) return;
        if ( x < 0 || x >= this.width || y < 0 || y >= this.height ) return;
        if ( this.disabled[ x ][ y ] ) return;
        if ( this.frozen[ x ][ y ] ) return;
        if ( this.cellState[ x ][ y ] !== CELL_HIDDEN ) return;

        // PATH mode: must be adjacent to a revealed cell
        if ( !this.canReveal( x, y ) ) return;

        // First click safety (CLEAR_ALL only)
        if ( this.firstClick )
        {
            this.firstClick = false;
            this.placeMines( x, y );
        }

        // Hit a mine
        if ( this.mines[ x ][ y ] )
        {
            this.gameOver = true;
            this.won = false;
            this.revealAll();
            return;
        }

        // Queue flood-fill reveal (works for both modes)
        this.floodReveal( x, y );
    }

    floodReveal( startX, startY )
    {
        const queue = [ [ startX, startY ] ];
        const visited = new Set();

        while ( queue.length > 0 )
        {
            const [ x, y ] = queue.shift();

            if ( x < 0 || x >= this.width || y < 0 || y >= this.height ) continue;
            if ( this.disabled[ x ][ y ] ) continue;
            const key = x + ',' + y;
            if ( visited.has( key ) ) continue;
            visited.add( key );
            if ( this.cellState[ x ][ y ] !== CELL_HIDDEN ) continue;
            if ( this.mines[ x ][ y ] ) continue;

            // Queue for animated reveal instead of revealing instantly
            this.pendingReveals.push( [ x, y ] );

            // If no adjacent mines, enqueue neighbors
            if ( this.adjacent[ x ][ y ] === 0 )
            {
                for ( let dx = -1; dx <= 1; dx++ )
                {
                    for ( let dy = -1; dy <= 1; dy++ )
                    {
                        if ( dx === 0 && dy === 0 ) continue;
                        queue.push( [ x + dx, y + dy ] );
                    }
                }
            }
        }
    }

    processPendingReveals( count, now )
    {
        const n = Math.min( count, this.pendingReveals.length );
        for ( let i = 0; i < n; i++ )
        {
            const [ x, y ] = this.pendingReveals.shift();
            this.cellState[ x ][ y ] = CELL_REVEALED;
            this.revealTimestamps[ x ][ y ] = now;
        }

        // Check win once queue is drained
        if ( this.pendingReveals.length === 0 && n > 0 )
        {
            this.checkWin();
        }
    }

    isAnimating()
    {
        return this.pendingReveals.length > 0;
    }

    toggleFlag( x, y )
    {
        if ( this.gameOver ) return;
        if ( x < 0 || x >= this.width || y < 0 || y >= this.height ) return;
        if ( this.disabled[ x ][ y ] ) return;
        if ( this.frozen[ x ][ y ] ) return;

        if ( this.cellState[ x ][ y ] === CELL_HIDDEN )
        {
            this.cellState[ x ][ y ] = CELL_FLAGGED;
        }
        else if ( this.cellState[ x ][ y ] === CELL_FLAGGED )
        {
            this.cellState[ x ][ y ] = CELL_HIDDEN;
        }
    }

    checkWin()
    {
        if ( this.gameType === GAME_PATH )
        {
            // PATH: any revealed, non-disabled cell in top row = win
            for ( let x = 0; x < this.width; x++ )
            {
                if ( !this.disabled[ x ][ this.height - 1 ] && this.cellState[ x ][ this.height - 1 ] === CELL_REVEALED )
                {
                    this.gameOver = true;
                    this.won = true;
                    return;
                }
            }
            return;
        }

        if ( this.gameType === GAME_FIND )
        {
            // FIND: win when diamond cell is revealed
            if ( this.diamondX >= 0 && this.cellState[ this.diamondX ][ this.diamondY ] === CELL_REVEALED )
            {
                this.gameOver = true;
                this.won = true;
            }
            return;
        }

        // CLEAR_ALL: all non-mine, non-disabled cells must be revealed
        for ( let x = 0; x < this.width; x++ )
        {
            for ( let y = 0; y < this.height; y++ )
            {
                if ( this.disabled[ x ][ y ] ) continue;
                if ( !this.mines[ x ][ y ] && this.cellState[ x ][ y ] !== CELL_REVEALED )
                {
                    return;
                }
            }
        }
        this.gameOver = true;
        this.won = true;
    }

    revealAll()
    {
        for ( let x = 0; x < this.width; x++ )
        {
            for ( let y = 0; y < this.height; y++ )
            {
                if ( this.disabled[ x ][ y ] ) continue;
                this.cellState[ x ][ y ] = CELL_REVEALED;
            }
        }
    }

    getCell( x, y )
    {
        return {
            state: this.cellState[ x ][ y ],
            hasMine: this.mines[ x ][ y ],
            adjacentCount: this.adjacent[ x ][ y ],
            disabled: this.disabled[ x ][ y ],
            frozen: this.frozen[ x ][ y ]
        };
    }
}
